import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { mgmKeys, retryMgmReward, useMgmHealth } from '../api/use-mgm-admin';

const STALE_TICK_MS = 5 * 60 * 1000;

function ago(value: string | null | undefined): string {
  if (!value) return 'nunca';
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return 'agora há pouco';
  if (minutes < 60) return `há ${minutes} min`;
  return `há ${Math.round(minutes / 60)} h`;
}

/**
 * Saúde do worker do MGM (só master). A recompensa é lançada por um job no
 * `epossible-dispatch-worker`; se ele parar, nada quebra na loja — o cliente
 * só não recebe. Esta tela é onde isso aparece.
 */
export function MgmHealthCard({ idPassbook }: { idPassbook: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useMgmHealth(idPassbook, true);
  const [retrying, setRetrying] = useState<string | null>(null);

  if (isLoading || !data) return <Skeleton className="h-28 rounded-2xl" />;

  const lastTick = data.worker?.lastTickAt ?? null;
  const workerStale = !lastTick || Date.now() - new Date(lastTick).getTime() > STALE_TICK_MS;
  const failed = data.rewards.failed;
  const healthy = !workerStale && failed.length === 0;

  const retry = async (id: string) => {
    setRetrying(id);
    try {
      await retryMgmReward(idPassbook, id);
      toast.success('Recompensa enviada para reprocessar.');
      await queryClient.invalidateQueries({ queryKey: mgmKeys.all(idPassbook) });
    } catch {
      toast.error('Não foi possível reprocessar.');
    } finally {
      setRetrying(null);
    }
  };

  return (
    <Card className={`p-5 ${healthy ? '' : 'border-destructive/60 bg-destructive/5'}`}>
      <div className="flex items-start gap-3">
        {healthy ? (
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" />
        ) : (
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {workerStale
              ? 'O worker do Indique e Ganhe não roda há mais de 5 minutos'
              : failed.length > 0
                ? `${failed.length} recompensa(s) com falha`
                : 'Recompensas em dia'}
          </p>
          <p className="text-sm text-muted-foreground">
            Último ciclo {ago(lastTick)} · {data.rewards.due} pronta(s) para lançar ·{' '}
            {data.rewards.waitingCondition} esperando condição · {data.referralsWatching} indicação(ões) em
            acompanhamento
          </p>
          {failed.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {failed.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border/60 bg-card p-3 text-sm">
                  <span className="min-w-0">
                    {f.role === 'referrer' ? 'Indicador' : 'Indicado'} · {f.amountText} ·{' '}
                    {f.failedOp === 'reverse' ? 'estorno' : 'crédito'}
                    <span className="block truncate text-xs text-muted-foreground">{f.lastError}</span>
                  </span>
                  <Button size="sm" variant="secondary" loading={retrying === f.id} onClick={() => void retry(f.id)}>
                    <RotateCcw className="size-4" /> Reprocessar
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
